// packages/requisites/src/index.ts
import _get from "lodash/get";
import _isObject from "lodash/isObject";
import {decamelize, capitalize as capitalize2} from "@shared/utils/string";
import capitalizeLodash from "lodash/capitalize";
import {degreeMapsUtils} from "@coursedog/degreeMaps";

// packages/requisites/src/advanceRequisiteUtils.js
import {capitalize} from "lodash";
function doPlural(word, number) {
  let singular = word;
  if (singular === "classes")
    singular = "class";
  if (singular === "credits")
    singular = "credit";
  if (number === 1)
    return singular;
  if (singular.charAt(singular.length - 1) === "s")
    return `${singular}es`;
  return `${singular}s`;
}
function grammarList(strings, conjunction) {
  if (strings.length === 0)
    return "";
  if (strings.length === 1)
    return strings[0];
  if (strings.length === 2)
    return `${strings[0]} ${conjunction} ${strings[1]}`;
  const list = strings.slice(0, strings.length - 1).join(", ");
  return `${list}, ${conjunction} ${strings[strings.length - 1]}`;
}
function advanceRequisiteUtils({courses, departments, localize}) {
  const courseLocalizedLabel = localize("course");
  const coursesLocalizedLabel = localize("course", "plural");
  const departmentLocalizedLabel = localize("department");
  const departmentsLocalizedLabel = localize("department", "plural");
  function getRelatedCourseById(courseId) {
    return courses[courseId] || {};
  }
  function getRelatedDepartmentById(departmentId) {
    const departmentsArr = Array.isArray(departments) ? departments : Object.values(departments || {});
    return departmentsArr.find((department) => department._id === departmentId) || {};
  }
  function courseLabel(course) {
    let label;
    if (Object.keys(course).length === 1 && course.courseId) {
      const courseData = getRelatedCourseById(course.courseId);
      label = `${courseData._id && `${courseData.subjectCode + courseData.courseNumber} - ${courseData.name}`}`;
    } else {
      const department = getRelatedDepartmentById(course.departmentId).displayName || course.subjectCode;
      if (course.type === "simple") {
        label = `${department} ${course.number || course.courseNumber}`;
      } else if (course.type === "range") {
        label = `${department} ${coursesLocalizedLabel} with a ${courseLocalizedLabel} number range from ${course.min} to ${course.max}`;
      } else if (course.type === "wildcard") {
        label = `a ${department} ${courseLocalizedLabel} starting with ${(course.number || course.courseNumber).split("@"[0])}`;
      }
      if (course.withConditions && course.withConditions.length) {
        label += ` where ${course.withConditions.map((c) => `${c.condition} ${c.comparator} ${c.value}`).join(", ")}`;
      }
    }
    return label || "Unknown Course";
  }
  function courseList(courses2, conjunction) {
    const labels = courses2.map((course) => courseLabel(course));
    return grammarList(labels, conjunction);
  }
  function conditionDisplayText(value) {
    const {
      condition,
      type,
      min,
      max,
      courses: courses2 = [],
      blocks = [],
      text,
      subRules
    } = value;
    const orList = courseList(courses2, "or");
    const andList = courseList(courses2, "and");
    const blockList = grammarList(blocks, "or");
    switch (condition) {
      case "transfer":
      case "passFail": {
        const suffix = condition === "transfer" ? "transfer" : "take as pass/fail";
        if (max === 0) {
          if (!courses2.length)
            return `No ${type} may be ${suffix}`;
          return `${courseList(courses2, "AND")} may not be ${suffix}`;
        } else {
          if (!courses2.length) {
            return `At most ${max} ${doPlural(type, max)} may be ${suffix}`;
          }
          return `At most ${max} ${doPlural(type, max)} from ${orList} may be ${suffix}`;
        }
      }
      case "residential":
      case "lastResidential": {
        const last = condition === "lastResidential" ? "the last " : "";
        return `At least ${last}${min} ${doPlural(type, min)} must be taken in residence`;
      }
      case "perDiscipline":
        if (max === void 0) {
          return `At least ${min} ${doPlural(type, min)} must be taken in each ${departmentLocalizedLabel}`;
        } else {
          if (min === void 0) {
            return `At most ${max} ${doPlural(type, max)} must be taken in each ${departmentLocalizedLabel}`;
          }
          return `Between ${min} and ${max} ${type} may be taken in each ${departmentLocalizedLabel}`;
        }
      case "perTerm":
        if (!courses2.length) {
          return `Only ${max} ${doPlural(type, max)} may be taken each term`;
        } else {
          return `Only ${max} ${doPlural(type, max)} from ${andList} may be taken each term`;
        }
      case "scribe":
        return `Completed Scribe block ${text}`;
      case "subRules": {
        const actualRules = subRules.filter((rule) => rule.condition !== "freeformText").length;
        if (max === void 0) {
          if (min === actualRules)
            return "Complete all of the following:";
          if (min === 1) {
            if (actualRules === 2)
              return "Complete either of the following:";
            return "Complete any of the following:";
          }
          if (min >= 1)
            return `Complete ${min} of the following:`;
          return;
        } else {
          if (min === void 0) {
            return `Complete at most ${max} of the following`;
          }
          return `Complete between ${min} and ${max} of the following`;
        }
      }
      case "spread":
        if (max === void 0) {
          return `${capitalize(coursesLocalizedLabel)} must be taken in at least ${min} different ${departmentsLocalizedLabel}`;
        } else {
          if (min === void 0) {
            return `${capitalize(coursesLocalizedLabel)} may be taken in at most ${max} different ${departmentsLocalizedLabel}`;
          }
          return `${capitalize(coursesLocalizedLabel)} must be taken in between ${min} and ${max} different ${departmentsLocalizedLabel}`;
        }
      case "grade":
        if (!courses2.length) {
          return `Only grades above ${min} will count for credit`;
        } else {
          return `Only grades above ${min} will count for credit in ${andList}`;
        }
      case "gpa":
        if (!courses2.length) {
          return `Must have a GPA of ${min} or above`;
        } else {
          return `Must have a GPA of ${min} or above in ${andList}`;
        }
      case "share":
        return `Credits used to satisfy this requirement may also be used to satisfy ${blockList} requirements`;
      case "complete":
        return `Requirement is complete`;
      case "incomplete":
        return `Requirement is NOT complete`;
      case "noncourse":
        if (min === void 0 || min === 1)
          return `Completed ${text}`;
        return `Completed ${text} ${min} times`;
      case "course":
        if (!courses2.length) {
          if (type === "credits") {
            if (max === void 0) {
              return `At least ${min} ${doPlural("credits", min)} must be taken`;
            }
            return `Between ${min} and ${max} credits must be taken`;
          }
          if (max !== void 0) {
            if (max === 0)
              return `Take no ${type}`;
            if (min === void 0) {
              return `At most ${max} ${doPlural(type, max)} will be counted`;
            }
            if (min === max) {
              return `Take exactly ${min} ${doPlural(type, max)}`;
            }
            return `Take between ${min} and ${max} ${type}`;
          }
        } else if (courses2.length === 1) {
          const courseStr = courseLabel(courses2[0]);
          if (min === 1)
            return `Take ${courseStr}`;
          if (max === 1)
            return `${courseStr} will not be counted`;
          if (min && type === "classes") {
            return `Take ${courseStr} ${min} ${doPlural("time", min)}`;
          }
          if (min && type === "credits") {
            return `Take ${courseStr} for ${min} credits`;
          }
        } else if (type === "classes") {
          if (max === void 0) {
            if (min === 1)
              return `Take ${orList}`;
            if (min === courses2.length)
              return `Take ${andList}`;
            return `Take ${min} classes from ${orList}`;
          } else {
            if (max === 0)
              return `${andList} will not be counted`;
            if (min === void 0) {
              return `Take at most ${max} ${doPlural("classes", max)} from ${orList}`;
            }
            return `Take between ${min} and ${max} classes from ${orList}`;
          }
        } else {
          if (max === void 0) {
            return `Take ${min} ${doPlural("credits", min)} from ${orList}`;
          }
          return `Take between ${min} and ${max} credits from ${orList}`;
        }
        return `Unable to understand the ${courseLocalizedLabel} rule`;
      case "credential": {
        const quantity = min || 1;
        if (quantity === 1) {
          if (blockList.length === 1)
            return `Completed a ${blocks[0]}`;
          return `Complete a ${blockList}`;
        }
        return `Complete ${min} ${blockList} credentials`;
      }
      case "if":
        return `If ${text}:`;
      case "elseIf":
        return `Otherwise, if ${text}:`;
      case "else":
        return `Otherwise:`;
      case "freeformText":
        return text || "";
      default:
        return "Unknown requirement condition";
    }
  }
  return {
    conditionDisplayText
  };
}

// packages/requisites/src/index.ts
var defaultLocalizationFn = (x) => x;
var DATA_SELECT_CONDITIONS = [
  "completed",
  "completedAllOf",
  "completedAnyOf",
  "completedAtLeastXOf",
  "enrolledIn",
  "minimumCredits",
  "minimumResidencyCredits",
  "minimumGrade",
  "averageGrade",
  "completeVariableCoursesAndVariableCredits"
];
function shouldShowDataSelect(rule) {
  const valueCondition = _get(rule, "value.condition", "");
  return DATA_SELECT_CONDITIONS.includes(rule.condition) && valueCondition !== "none";
}
function simpleRequisiteDataSelectDisplayString(selection) {
  switch (selection.condition) {
    case "anyOf":
      return "Any of:";
    case "allOf":
      return "All of:";
    case "numberOf":
      return `At least ${selection.number} of:`;
    case "freeformText":
      return selection.text ? `- ${selection.text}` : "";
    case "courses":
    case "programs":
    case "courseSets":
    case "requisiteSets":
      return "";
    default:
      return `These ${decamelize(selection.condition).toLowerCase()}:`;
  }
}
function getNormalizedValues(values) {
  return values.map((v) => _isObject(v) ? v : {value: [v], logic: "and"});
}
function requisiteUtils({courses, programs, courseSets, requisiteSets, departments, isCatalog, localize = defaultLocalizationFn} = {courses: {}, programs: {}, courseSets: {}, requisiteSets: {}, departments: {}, isCatalog: false, localize: defaultLocalizationFn}, dateRanges = "2200-01-01;") {
  const programLabel = localize("program");
  const courseLabel = localize("course");
  const coursesLabel = localize("course", "plural");
  const courseSetLabel = localize("course set");
  function simpleRequisiteDisplayString(rule) {
    const valueCondition = _get(rule, "value.condition", "");
    const displayString = valueCondition === "requisiteSets" ? "Requirement Sets" : capitalize2(localize(valueCondition));
    switch (rule.condition) {
      case "anyOf":
        return "Fulfill ANY of the following requirements:";
      case "allOf":
        return "Fulfill ALL of the following requirements:";
      case "numberOf":
        return `Complete at least ${rule.number} of the following:`;
      case "freeformText":
        return rule.value ? `${rule.value}` : "";
      case "minimumGPA":
        return `Earn a minimum GPA of ${rule.value}.`;
      case "minimumCredits":
        const minimumCreditsString = `Earn at least ${rule.credits} credits`;
        if (valueCondition === "none")
          return minimumCreditsString;
        else
          return `${minimumCreditsString} from the following:`;
      case "minimumResidencyCredits":
        const minimumResidencyCreditsString = `Earn at least ${rule.credits} credits in residence`;
        if (valueCondition === "none")
          return minimumResidencyCreditsString;
        else
          return `${minimumResidencyCreditsString} from the following:`;
      case "minimumGrade":
        const minimumGradeString = `Earn a minimum ${rule.gradeType || "grade"} of ${rule.grade}`;
        if (valueCondition === "none")
          return minimumGradeString;
        else
          return `${minimumGradeString} in the following:`;
      case "averageGrade":
        const averageGradeString = `Earn an average ${rule.gradeType || "grade"} of ${rule.grade}`;
        if (valueCondition === "none")
          return averageGradeString;
        else
          return `${averageGradeString} in the following:`;
      case "completed":
        return "Complete the following:";
      case "completedAllOf":
        return `Complete ALL of the following ${displayString}:`;
      case "completedAnyOf":
        return `Complete ANY of the following ${displayString}:`;
      case "completedAtLeastXOf":
        if (valueCondition === "courseSets") {
          return `Complete at least ${rule.restriction || 0} ${coursesLabel} in the following
          ${displayString.toLowerCase()}:`;
        } else {
          return `Complete at least ${rule.restriction || 0} of the following
        ${displayString.toLowerCase()}:`;
        }
      case "enrolledIn":
        return `Enroll in the following ${displayString}:`;
      case "completeVariableCoursesAndVariableCredits":
        let coursesString = "";
        if (rule.minCourses && rule.maxCourses) {
          if (rule.minCourses === rule.maxCourses) {
            coursesString = `exactly ${rule.minCourses}`;
          } else {
            coursesString = `${rule.minCourses} - ${rule.maxCourses}`;
          }
        } else if (rule.minCourses && !rule.maxCourses) {
          coursesString = `${rule.minCourses} or more`;
        } else if (!rule.minCourses && rule.maxCourses) {
          coursesString = `up to ${rule.maxCourses}`;
        }
        let creditsString = "";
        if (rule.minCredits && rule.maxCredits) {
          if (rule.minCredits === rule.maxCredits) {
            creditsString = `exactly ${rule.minCredits}`;
          } else {
            creditsString = `${rule.minCredits} - ${rule.maxCredits}`;
          }
        } else if (rule.minCredits && !rule.maxCredits) {
          creditsString = `${rule.minCredits} or more`;
        } else if (!rule.minCredits && rule.maxCredits) {
          creditsString = `up to ${rule.maxCredits}`;
        }
        return `Complete ${coursesString} ${courseLabel}(s) and earn ${creditsString} credit(s) from the following:`;
      default:
        return "Requisite Condition Not Found";
    }
  }
  function getFilteredRequisites(requisites = []) {
    if (isCatalog) {
      const filteredRequisites = requisites.filter((requisite) => requisite.showInCatalog);
      return filteredRequisites;
    }
    return requisites;
  }
  function isNestedFieldVisibleInPdf(fieldConfig = {}) {
    const questionVisibility = fieldConfig.pdfVisibility;
    return !isCatalog || !questionVisibility || questionVisibility.includes("content");
  }
  function visibleRequisites({requisitesSimple, requisitesAdvanced, requisitesFreeform} = {}, question) {
    const visibleRequisitesData = {};
    const nestedFields = question?.config?.fields || {requisitesAdvanced: {}, requisitesFreeform: {}, requisitesSimple: {}};
    const filteredAdvancedRequisites = getFilteredRequisites(requisitesAdvanced);
    const filteredSimpleRequisites = getFilteredRequisites(requisitesSimple);
    if (requisitesFreeform && (!isCatalog || requisitesFreeform.showInCatalog && isNestedFieldVisibleInPdf(nestedFields.requisitesFreeform))) {
      visibleRequisitesData["requisitesFreeform"] = requisitesFreeform;
    }
    if (filteredAdvancedRequisites.length && isNestedFieldVisibleInPdf(nestedFields.requisitesAdvanced)) {
      visibleRequisitesData["requisitesAdvanced"] = filteredAdvancedRequisites;
    }
    if (filteredSimpleRequisites.length && isNestedFieldVisibleInPdf(nestedFields.requisitesSimple)) {
      visibleRequisitesData["requisitesSimple"] = filteredSimpleRequisites;
    }
    return visibleRequisitesData;
  }
  function _getProgramsByGroupId(programId) {
    const [startDate] = dateRanges.split(";");
    const programsWithSameProgramGroupId = Object.values(programs || {}).filter((program) => (program.programGroupId === programId || program.deprecatedProgramGroupId === programId) && (program.effectiveStartDate || startDate) <= startDate).sort((programA, programB) => (programA.effectiveStartDate || "") < (programB.effectiveStartDate || "") ? 1 : -1);
    return programsWithSameProgramGroupId.length ? programsWithSameProgramGroupId[0] : {};
  }
  function _getCourseSetLabel(courseSetId) {
    const courseSet = courseSets[courseSetId] || {};
    return courseSet.name || `Untitled ${capitalizeLodash(courseSetLabel)}`;
  }
  function _getRequisiteSetLabel(requisiteSetId) {
    const requisiteSet = requisiteSets[requisiteSetId] || {};
    return requisiteSet.name || "Untitled Requirement Set";
  }
  function _getCourseLabel(courseId, options) {
    const dmUtils = degreeMapsUtils({courses, options});
    return dmUtils.getCourseName(courseId, options);
  }
  function _getProgramLabel(programId, options) {
    const program = _getProgramsByGroupId(programId);
    if (Object.keys(program).length) {
      const code = program.code;
      const name = program.transcriptDescription || program.longName || program.name || `Untitled ${capitalize2(programLabel)}`;
      const appendRequestStatus = ["pending", "suspended", "rejected"].includes(program.requestStatus);
      if (appendRequestStatus) {
        return `${code} - ${name} (${program.requestStatus} in workflow)`;
      }
      if (program.status === "Inactive") {
        return `${code} - ${name} (Inactive)`;
      }
      return `${code} - ${name}`;
    }
    if (!program && options?.displayRequisiteIdInlineIfEntityNotFound)
      return programId;
    return "";
  }
  function getCoursesOutput(value, options) {
    return value.value.map((courseId) => _getCourseLabel(courseId, options)).join(` </br><strong>${value.logic.toUpperCase()}</strong> `);
  }
  function getProgramsOutput(value, options) {
    return value.value.map((programId) => _getProgramLabel(programId, options)).join(` </br><strong>${value.logic.toUpperCase()}</strong> `);
  }
  function getCourseSetsOutput(value) {
    return value.value.map((courseSetId) => _getCourseSetLabel(courseSetId)).join(` </br><strong>${value.logic.toUpperCase()}</strong> `);
  }
  function getRequisiteSetsOutput(value) {
    return value.value.map((requisiteSetId) => _getRequisiteSetLabel(requisiteSetId)).join(` </br><strong>${value.logic.toUpperCase()}</strong> `);
  }
  const {conditionDisplayText} = advanceRequisiteUtils({courses, departments, localize});
  return {
    visibleRequisites,
    simpleRequisiteDisplayString,
    simpleRequisiteDataSelectDisplayString,
    shouldShowDataSelect,
    getNormalizedValues,
    getCoursesOutput,
    getProgramsOutput,
    getCourseSetsOutput,
    getRequisiteSetsOutput,
    conditionDisplayText
  };
}
export {
  defaultLocalizationFn,
  requisiteUtils
};
